// i18n (国際化) ユーティリティ

import type { SettingsData, SettingItem } from '../types/index.js';

// 翻訳データの型定義
type TranslationKey = 
    | 'appTitle' | 'appSubtitle'
    | 'searchPlaceholder' | 'expandAll' | 'expandAllTitle' | 'collapseAll' | 'collapseAllTitle' | 'clearSelection' | 'clearSelectionTitle' | 'selectedCount'
    | 'guideTitle' | 'guideEfficiency' | 'guideEfficiencyDesc' | 'guideBasicUsage' | 'guideStep1' | 'guideStep2' | 'guideStep3'
    | 'guideKittingFlow' | 'guideKittingStep1' | 'guideKittingStep2' | 'guideKittingStep3'
    | 'guideAppImport' | 'guideAppImportDesc' | 'guideAppImportStep1' | 'guideAppImportStep2' | 'guideAppImportStep3' | 'guideAppImportStep4'
    | 'guideRevert' | 'guideRevertDesc' | 'guideRevertStep1' | 'guideRevertStep2' | 'guideRevertStep3'
    | 'guideWarning' | 'guideWarning1' | 'guideWarning2' | 'guideWarning3'
    | 'previewTitle' | 'copyButton' | 'copySuccess' | 'downloadButton' | 'downloadRevertButton' | 'downloadRevertTitle'
    | 'previewTip' | 'previewRevertTip'
    | 'adminBadge' | 'adminBadgeTitle' | 'rebootBadge' | 'rebootBadgeTitle' | 'revertBadge' | 'revertBadgeTitle'
    | 'docsButton' | 'docsButtonTitle' | 'adminWarningTitle'
    | 'treeInputPlaceholder' | 'importButton' | 'importStatusEmpty' | 'importStatusNoExe' | 'importStatusSuccess' | 'importError'
    | 'addNIC' | 'nicAdapterName' | 'nicIPAddress' | 'nicSubnetMask' | 'nicGateway' | 'nicDNSPrimary' | 'nicDNSSecondary'
    | 'nicNotConfigured' | 'nicDelete' | 'nicAdapterNameLabel' | 'nicConfigTypeLabel' | 'nicStaticIP' | 'nicDHCP'
    | 'nicIPAddressLabel' | 'nicSubnetMaskLabel' | 'nicGatewayLabel' | 'nicDNSLabel' | 'nicDNSPrimaryLabel' | 'nicDNSSecondaryLabel' | 'nicAutocompleteLabel'
    | 'variableSettings' | 'codePreview' | 'codePreviewEmpty'
    | 'docsDrawerTitle' | 'docsWhatIs' | 'docsCategory' | 'docsSection'
    | 'docsOverview' | 'docsGeneratedCode' | 'docsNoCodeOutput' | 'docsDetailedDescription' | 'docsCommandExplanation'
    | 'docsAdminRequired' | 'docsAdminRequiredDesc' | 'docsRebootRequired' | 'docsRebootRequiredDesc'
    | 'docsRevertAvailable' | 'docsRevertAvailableDesc' | 'docsDynamicSetting' | 'docsDynamicSettingDesc'
    | 'docsUsageScenarios' | 'docsUsageScenario1' | 'docsUsageScenario2' | 'docsUsageScenario3' | 'docsUsageScenario4'
    | 'docsWarning' | 'docsWarningDesc'
    | 'settingsLoadError'
    | 'language' | 'languageJa' | 'languageEn'
    | 'headerOpenTable' | 'headerOpenTableNewWindow' | 'headerEditing' | 'headerPrevPC' | 'headerNextPC'
    | 'tableEditorPageTitle' | 'tableEditorSubtitle' | 'tableEditorBackToMain' | 'tableEditorBackToMainTitle'
    | 'tableEditorModalTitle'
    | 'tableEditorHelpText' | 'tableEditorAddPC' | 'tableEditorClearTable' | 'tableEditorSettingVariableHeader'
    | 'tableEditorPcNamePlaceholder' | 'tableEditorSelect' | 'tableEditorSelected' | 'tableEditorSelectTitle' | 'tableEditorDeleteTitle'
    | 'tableEditorValuePlaceholder' | 'tableEditorNoSettingsLoaded' | 'tableEditorNoPcs' | 'tableEditorClearConfirm'
    | 'tableBatchSettingsNotLoaded' | 'tableBatchNoPcs' | 'tableBatchConfirmMultiDownload' | 'tableBatchDownloadComplete'
    | 'appImportTitle' | 'appImportUsage' | 'appImportTip' | 'appImportStep1' | 'appImportStep2' | 'appImportStep3' | 'appImportStep4'
    | 'clearButton' | 'copyError'
    | 'batchAdminSelfElevate' | 'batchAdminNotElevated' | 'batchAdminElevated' | 'batchAdminCheck' | 'batchAdminChecking'
    | 'batchAdminConfirmed' | 'batchAdminRequired' | 'batchAdminCheckComment' | 'batchAdminRequiredMessage'
    | 'batchApplicationInstall' | 'batchSetupStart' | 'batchSetupComplete' | 'batchLogConfig' | 'batchLogFile'
    | 'batchRevert' | 'batchRevertComplete' | 'batchRevertSettings' | 'batchApplyingSettings'
    | 'revert';

type Translations = Record<TranslationKey, string>;

// 設定項目ごとのテキスト（id を主キーとする多言語テキスト）
// 将来的に DB の (setting_id, lang) に対応させることを想定
export interface SettingText {
    title?: string;
    description?: string;
    code?: string;
    revertCode?: string;
}

// 設定項目テキスト用の i18n 辞書
// 例: settingTexts['ja']['ie_homepage_blank']?.title
// 現状は空オブジェクトとしておき、段階的にここへ寄せていく
const settingTexts: Record<'ja' | 'en', Record<string, SettingText>> = {
    ja: {},
    en: {}
};

// 翻訳データ
const translations: Record<'ja' | 'en', Translations> = {
    ja: {
        // ヘッダー
        appTitle: "WIN BATCH GEN",
        appSubtitle: "Windows キッティング・PC設定のバッチファイル自動生成ツール",
        
        // 検索・操作
        searchPlaceholder: "設定を検索...",
        expandAll: "全展開",
        expandAllTitle: "全て展開",
        collapseAll: "全折りたたみ",
        collapseAllTitle: "全て折りたたみ",
        clearSelection: "全解除",
        clearSelectionTitle: "チェックを全て外す",
        selectedCount: "選択中: {count}件",
        
        // ガイド
        guideTitle: "初めての方へ：使い方ガイド",
        guideEfficiency: "キッティング作業を効率化",
        guideEfficiencyDesc: "新規PCの設定や複数台の一括設定に最適です。バッチファイルの知識がなくても、チェックボックスを選ぶだけで設定用のバッチファイルを自動生成できます。生成されたバッチファイルは、右側のプレビューで内容を確認してからダウンロードしてください。",
        guideBasicUsage: "📋 基本的な使い方（3ステップ）",
        guideStep1: "左側の設定項目から、適用したい設定にチェックを入れます",
        guideStep2: "右側のプレビューで生成されたバッチファイルの内容を確認します",
        guideStep3: "「ダウンロード (.bat)」ボタンでバッチファイルを保存します",
        guideKittingFlow: "💡 キッティング作業の流れ",
        guideKittingStep1: "まず1台のPCでテスト：生成したバッチファイルを1台のPCで実行して動作確認",
        guideKittingStep2: "問題なければ複数台に展開：同じバッチファイルを他のPC（2台目以降）にコピーして実行",
        guideKittingStep3: "設定を元に戻したい場合：「↩ 可逆設定を元に戻すBatをDL」ボタンで、「可逆設定有」バッジがある設定を元に戻すバッチファイル（revert.bat）を生成できます",
        guideAppImport: "📦 インストーラー取り込み機能",
        guideAppImportDesc: "インストーラーフォルダ内の .exe ファイルを一括で取り込み、自動的にインストールコマンドを推測します。",
        guideAppImportStep1: "インストーラーフォルダでコマンドプロンプトを開く",
        guideAppImportStep2: "tree /F を実行して出力をコピー",
        guideAppImportStep3: "「アプリケーション」セクションのテキストエリアに貼り付けて「インポート」をクリック",
        guideAppImportStep4: "「インポート」ボタンをクリック",
        guideRevert: "↩ 可逆設定を元に戻す機能",
        guideRevertDesc: "「可逆設定有」バッジ（可逆設定有）がある設定項目を元の状態に戻すことができます：",
        guideRevertStep1: "「↩ 可逆設定を元に戻すBatをDL」ボタンで revert.bat を生成",
        guideRevertStep2: "revert.bat を実行すると、選択した「可逆設定有」の設定項目が元の状態に戻ります",
        guideRevertStep3: "「可逆設定有」バッジがない設定項目は元に戻すことができません",
        guideWarning: "⚠️ 注意事項",
        guideWarning1: "バッチファイルをダブルクリックで実行すると、管理者権限が必要な場合は自動的に昇格します",
        guideWarning2: "複数台に適用する前に、必ず1台でテストしてください",
        guideWarning3: "各設定項目の「📖」ボタンで詳細を確認できます",
        
        // プレビュー
        previewTitle: "PREVIEW: setup.bat",
        copyButton: "コピー",
        copySuccess: "コピーしました！",
        downloadButton: "ダウンロード (.bat)",
        downloadRevertButton: "↩ 可逆設定を元に戻すBatをDL",
        downloadRevertTitle: "「可逆設定有」バッジがある設定を元に戻すバッチファイルを生成します",
        previewTip: "💡 ダウンロードしたバッチファイルをダブルクリックで実行すると、管理者権限が必要な場合は自動的に昇格します。複数台に適用する場合は、まず1台でテストしてから展開してください。",
        previewRevertTip: "↩ 「可逆設定を元に戻すBatをDL」ボタンで、「可逆設定有」バッジがある設定項目を元の状態に戻すバッチファイル（revert.bat）を生成できます。",
        
        // バッジ・ラベル
        adminBadge: "管理者",
        adminBadgeTitle: "管理者権限が必要です",
        rebootBadge: "再起動",
        rebootBadgeTitle: "再起動が必要です",
        revertBadge: "可逆設定有",
        revertBadgeTitle: "元に戻すバッチファイルが生成可能です",
        docsButton: "Docs",
        docsButtonTitle: "詳細ドキュメントを表示",
        adminWarningTitle: "管理者権限が必要な設定が有効なため、自動的に管理者権限チェックが追加されます",
        
        // アプリケーションインポート
        treeInputPlaceholder: "tree /F の出力をここに貼り付け...",
        importButton: "インポート",
        importStatusEmpty: "Treeコマンドの出力を入力してください",
        importStatusNoExe: ".exeファイルが見つかりませんでした",
        importStatusSuccess: "{count}個のアプリケーションを検出しました（インストーラーパス: {path}）",
        importError: "解析エラー: {error}",
        
        // NIC設定
        addNIC: "+ NICを追加",
        nicAdapterName: "イーサネット",
        nicIPAddress: "192.168.1.100",
        nicSubnetMask: "255.255.255.0",
        nicGateway: "192.168.1.1（空欄可）",
        nicDNSPrimary: "192.168.1.1（空欄可）",
        nicDNSSecondary: "8.8.8.8（空欄可）",
        nicNotConfigured: "NICが設定されていません。追加ボタンから追加してください。",
        nicDelete: "削除",
        nicAdapterNameLabel: "アダプタ名",
        nicConfigTypeLabel: "設定タイプ",
        nicStaticIP: "固定IP",
        nicDHCP: "DHCP",
        nicIPAddressLabel: "IPアドレス",
        nicSubnetMaskLabel: "サブネットマスク",
        nicGatewayLabel: "ゲートウェイ（空欄で設定しない）",
        nicDNSLabel: "DNS（空欄で設定しない）",
        nicDNSPrimaryLabel: "プライマリDNS",
        nicDNSSecondaryLabel: "セカンダリDNS",
        nicAutocompleteLabel: "補完候補:",
        
        // 変数設定
        variableSettings: "変数設定:",
        codePreview: "bat&gt;",
        codePreviewEmpty: "bat&gt; (この項目はバッチ本体には直接コードを出力しません)",
        
        // ドキュメント
        docsDrawerTitle: "{itemTitle} / {categoryTitle}",
        docsWhatIs: "{itemTitle}とは",
        docsCategory: "カテゴリ:",
        docsSection: "セクション:",
        docsOverview: "概要",
        docsGeneratedCode: "生成されるバッチスクリプト",
        docsNoCodeOutput: "バッチコードはこのオプションでは直接出力されません。",
        docsDetailedDescription: "詳細説明",
        docsCommandExplanation: "コマンドの各部分の意味",
        docsAdminRequired: "⚠ 管理者権限必須:",
        docsAdminRequiredDesc: "この設定を適用するには管理者権限が必要です。「管理者権限で実行」オプションを有効にしてください。",
        docsRebootRequired: "🔄 再起動が必要:",
        docsRebootRequiredDesc: "この設定を適用した後、PCの再起動が必要です。変更が完全に反映されるまで再起動してください。",
        docsRevertAvailable: "↩ 可逆設定有:",
        docsRevertAvailableDesc: "この設定は元に戻すことができます。「↩ 可逆設定を元に戻すBatをDL」ボタンから元の状態に戻すバッチファイルを生成できます。",
        docsDynamicSetting: "📋 動的設定:",
        docsDynamicSettingDesc: "この設定は複数の項目を追加できます。必要に応じて「+ NICを追加」ボタンなどから項目を追加してください。",
        docsUsageScenarios: "使用シーン:",
        docsUsageScenario1: "新規PCのキッティング作業（1台〜複数台）",
        docsUsageScenario2: "複数台への一括設定展開（同じ設定を複数PCに適用）",
        docsUsageScenario3: "標準化された環境構築（全社で統一された設定）",
        docsUsageScenario4: "バッチファイルを書けない人でも簡単に設定ファイルを作成",
        docsWarning: "注意:",
        docsWarningDesc: "この設定はレジストリやシステム設定を変更します。本番環境に適用する前に、必ずテスト環境（1台のPC）で動作確認を行ってください。特に複数台に適用する場合は、まず1台でテストしてから展開してください。",
        
        // エラー
        settingsLoadError: "設定データの読み込みに失敗しました: {status}",
        
        // 言語切り替え
        language: "言語",
        languageJa: "日本語",
        languageEn: "English",
        headerOpenTable: "📊 テーブルを開く",
        headerOpenTableNewWindow: "🗔 別ウィンドウ",
        headerEditing: "編集中:",
        headerPrevPC: "前のPCに移動",
        headerNextPC: "次のPCに移動",

        // テーブルエディタ
        tableEditorPageTitle: "テーブルエディタ | WIN BATCH GEN",
        tableEditorSubtitle: "テーブルエディタ（PC別設定専用ビュー）",
        tableEditorModalTitle: "📊 テーブルエディタ（PC別設定）",
        tableEditorBackToMain: "メインに戻る",
        tableEditorBackToMainTitle: "メイン画面を開く（同じタブ）",
        tableEditorHelpText: "各列が1台のPCを表します。行ごとに設定および変数を編集できます。",
        tableEditorAddPC: "PCを追加",
        tableEditorClearTable: "テーブルをクリア",
        tableEditorSettingVariableHeader: "設定 / 変数",
        tableEditorPcNamePlaceholder: "PC名",
        tableEditorSelect: "選択",
        tableEditorSelected: "選択中",
        tableEditorSelectTitle: "このPCを選択してメイン画面に反映",
        tableEditorDeleteTitle: "PCを削除",
        tableEditorValuePlaceholder: "値",
        tableEditorNoSettingsLoaded: "設定が読み込まれていません。",
        tableEditorNoPcs: "PCが登録されていません。「PCを追加」ボタンでPCを追加してください。",
        tableEditorClearConfirm: "テーブルのデータをすべて削除しますか？",

        // テーブル→複数バッチ生成
        tableBatchSettingsNotLoaded: "設定データが読み込まれていません",
        tableBatchNoPcs: "テーブルにPCが登録されていません。「PCを追加」ボタンでPCを追加してください。",
        tableBatchConfirmMultiDownload: "{count}個のバッチファイルを生成します。\n\n各ファイルを順番にダウンロードします。\n\n続行しますか？",
        tableBatchDownloadComplete: "すべてのファイルのダウンロードが完了しました。",
        
        // インストーラー取り込み
        appImportTitle: "インストーラー取り込み",
        appImportUsage: "📋 使い方:",
        appImportTip: "💡 インストーラーフォルダ内の .exe ファイルが自動検出され、インストールコマンドが推測されます",
        appImportStep1: "インストーラーフォルダでコマンドプロンプトを開く",
        appImportStep2: "tree /F を実行して出力をコピー",
        appImportStep3: "「アプリケーション」セクションのテキストエリアに貼り付けて「インポート」をクリック",
        appImportStep4: "「インポート」ボタンをクリック",
        clearButton: "クリア",
        copyError: "コピーに失敗しました",
        
        // バッチコード内のメッセージ
        batchAdminSelfElevate: "管理者権限で自己昇格",
        batchAdminNotElevated: "NG: 現在は昇格されていません。管理者として再実行します...",
        batchAdminElevated: "OK: 管理者権限で実行中です。",
        batchAdminCheck: "管理者権限チェック",
        batchAdminChecking: "管理者権限チェック...",
        batchAdminConfirmed: "OK: 管理者権限を確認しました。",
        batchAdminRequired: "管理者権限が必要です",
        batchAdminCheckComment: "管理者権限チェック",
        batchAdminRequiredMessage: "管理者権限が必要です。昇格します...",
        batchApplicationInstall: "アプリケーションインストール",
        batchSetupStart: "PC セットアップ開始",
        batchSetupComplete: "セットアップ完了",
        batchLogConfig: "ログ設定",
        batchLogFile: "ログファイル: %LOG%",
        batchRevert: "元に戻す",
        batchRevertComplete: "元に戻す処理完了",
        batchRevertSettings: "設定を元に戻す",
        batchApplyingSettings: "設定を反映中...",
        revert: "元に戻す"
    },
    en: {
        // Header
        appTitle: "WIN BATCH GEN",
        appSubtitle: "Windows Kitting & PC Setup Batch File Generator",
        
        // Search & Actions
        searchPlaceholder: "Search settings...",
        expandAll: "Expand",
        expandAllTitle: "Expand all",
        collapseAll: "Collapse",
        collapseAllTitle: "Collapse all",
        clearSelection: "Clear",
        clearSelectionTitle: "Clear all checks",
        selectedCount: "Selected: {count}",
        
        // Guide
        guideTitle: "First Time User Guide",
        guideEfficiency: "Streamline Kitting Work",
        guideEfficiencyDesc: "Perfect for setting up new PCs or batch configuration of multiple machines. You can automatically generate setup batch files by simply checking boxes, even without batch file knowledge. Review the generated batch file content in the preview on the right before downloading.",
        guideBasicUsage: "📋 Basic Usage (3 Steps)",
        guideStep1: "Check the settings you want to apply from the left panel",
        guideStep2: "Review the generated batch file content in the preview on the right",
        guideStep3: "Save the batch file using the \"Download (.bat)\" button",
        guideKittingFlow: "💡 Kitting Workflow",
        guideKittingStep1: "Test on one PC first: Run the generated batch file on one PC to verify it works",
        guideKittingStep2: "If successful, deploy to multiple PCs: Copy the same batch file to other PCs (2nd and beyond) and run it",
        guideKittingStep3: "To revert settings: Use the \"↩ Download Revert Bat\" button to generate a revert.bat file that restores settings marked with the \"Reversible\" badge",
        guideAppImport: "📦 Application Import Feature",
        guideAppImportDesc: "Bulk import .exe files from an installer folder and automatically guess installation commands.",
        guideAppImportStep1: "Open Command Prompt in the installer folder",
        guideAppImportStep2: "Run tree /F and copy the output",
        guideAppImportStep3: "Paste it into the text area in the \"Applications\" section and click \"Import\"",
        guideAppImportStep4: "Click the \"Import\" button",
        guideRevert: "↩ Revert Reversible Settings",
        guideRevertDesc: "You can restore settings marked with the \"Reversible\" badge to their original state:",
        guideRevertStep1: "Generate revert.bat using the \"↩ Download Revert Bat\" button",
        guideRevertStep2: "Running revert.bat will restore selected settings marked with \"Reversible\" to their original state",
        guideRevertStep3: "Settings without the \"Reversible\" badge cannot be reverted",
        guideWarning: "⚠️ Important Notes",
        guideWarning1: "Double-clicking the batch file will automatically elevate to admin if needed",
        guideWarning2: "Always test on one PC before deploying to multiple machines",
        guideWarning3: "Click the \"📖\" button on each setting to view details",
        
        // Preview
        previewTitle: "PREVIEW: setup.bat",
        copyButton: "Copy",
        copySuccess: "Copied!",
        downloadButton: "Download (.bat)",
        downloadRevertButton: "↩ Download Revert Bat",
        downloadRevertTitle: "Generate a batch file to revert settings marked with \"Reversible\" badge",
        previewTip: "💡 Double-clicking the downloaded batch file will automatically elevate to admin if needed. When deploying to multiple PCs, test on one first before expanding.",
        previewRevertTip: "↩ Use the \"↩ Download Revert Bat\" button to generate a revert.bat file that restores settings marked with the \"Reversible\" badge to their original state.",
        
        // Badges & Labels
        adminBadge: "Admin",
        adminBadgeTitle: "Administrator privileges required",
        rebootBadge: "Reboot",
        rebootBadgeTitle: "Reboot required",
        revertBadge: "Reversible",
        revertBadgeTitle: "Revert batch file can be generated",
        docsButton: "Docs",
        docsButtonTitle: "Show detailed documentation",
        adminWarningTitle: "Admin check will be automatically added because settings requiring admin privileges are enabled",
        
        // Installer Import
        appImportTitle: "Installer Import",
        appImportUsage: "📋 Usage:",
        appImportTip: "💡 .exe files in the installer folder will be automatically detected and installation commands will be guessed",
        appImportStep1: "Open Command Prompt in the installer folder",
        appImportStep2: "Run tree /F and copy the output",
        appImportStep3: "Paste it into the text area in the \"Applications\" section and click \"Import\"",
        appImportStep4: "Click the \"Import\" button",
        clearButton: "Clear",
        copyError: "Copy failed",
        treeInputPlaceholder: "Paste tree /F output here...",
        importButton: "Import",
        importStatusEmpty: "Please enter tree command output",
        importStatusNoExe: "No .exe files found",
        importStatusSuccess: "Detected {count} applications (Installer path: {path})",
        importError: "Parse error: {error}",
        
        // Batch Code Messages
        batchAdminSelfElevate: "Self-elevate with administrator privileges",
        batchAdminNotElevated: "NG: Not currently elevated. Re-running as administrator...",
        batchAdminElevated: "OK: Running with administrator privileges.",
        batchAdminCheck: "Administrator Privilege Check",
        batchAdminChecking: "Checking administrator privileges...",
        batchAdminConfirmed: "OK: Administrator privileges confirmed.",
        batchAdminRequired: "Administrator privileges required",
        batchAdminCheckComment: "Administrator Privilege Check",
        batchAdminRequiredMessage: "Administrator privileges required. Elevating...",
        batchApplicationInstall: "Application Installation",
        batchSetupStart: "PC Setup Started",
        batchSetupComplete: "Setup Complete",
        batchLogConfig: "Log Configuration",
        batchLogFile: "Log file: %LOG%",
        batchRevert: "Revert",
        batchRevertComplete: "Revert Process Complete",
        batchRevertSettings: "Revert Settings",
        batchApplyingSettings: "Applying settings...",
        revert: "Revert",
        
        // NIC Settings
        addNIC: "+ Add NIC",
        nicAdapterName: "Ethernet",
        nicIPAddress: "192.168.1.100",
        nicSubnetMask: "255.255.255.0",
        nicGateway: "192.168.1.1 (optional)",
        nicDNSPrimary: "192.168.1.1 (optional)",
        nicDNSSecondary: "8.8.8.8 (optional)",
        nicNotConfigured: "No NIC configured. Please add one using the Add button.",
        nicDelete: "Delete",
        nicAdapterNameLabel: "Adapter Name",
        nicConfigTypeLabel: "Config Type",
        nicStaticIP: "Static IP",
        nicDHCP: "DHCP",
        nicIPAddressLabel: "IP Address",
        nicSubnetMaskLabel: "Subnet Mask",
        nicGatewayLabel: "Gateway (leave blank to not set)",
        nicDNSLabel: "DNS (leave blank to not set)",
        nicDNSPrimaryLabel: "Primary DNS",
        nicDNSSecondaryLabel: "Secondary DNS",
        nicAutocompleteLabel: "Suggestions:",
        
        // Variable Settings
        variableSettings: "Variable Settings:",
        codePreview: "bat&gt;",
        codePreviewEmpty: "bat&gt; (This item does not output code directly to the batch file)",
        
        // Documentation
        docsDrawerTitle: "{itemTitle} / {categoryTitle}",
        docsWhatIs: "What is {itemTitle}",
        docsCategory: "Category:",
        docsSection: "Section:",
        docsOverview: "Overview",
        docsGeneratedCode: "Generated Batch Script",
        docsNoCodeOutput: "Batch code is not directly output for this option.",
        docsDetailedDescription: "Detailed Description",
        docsCommandExplanation: "Meaning of Each Part of the Command",
        docsAdminRequired: "⚠ Admin Rights Required:",
        docsAdminRequiredDesc: "Administrator privileges are required to apply this setting. Please enable the \"Run as Administrator\" option.",
        docsRebootRequired: "🔄 Reboot Required:",
        docsRebootRequiredDesc: "A PC reboot is required after applying this setting. Please reboot to ensure changes are fully applied.",
        docsRevertAvailable: "↩ Reversible Setting:",
        docsRevertAvailableDesc: "This setting can be reverted. You can generate a batch file to restore the original state from the \"↩ Download Revert Batch\" button.",
        docsDynamicSetting: "📋 Dynamic Setting:",
        docsDynamicSettingDesc: "This setting allows adding multiple items. Add items as needed from buttons like \"+ Add NIC\".",
        docsUsageScenarios: "Usage Scenarios:",
        docsUsageScenario1: "PC kitting work (1 to multiple PCs)",
        docsUsageScenario2: "Bulk deployment to multiple PCs (applying the same settings to multiple PCs)",
        docsUsageScenario3: "Standardized environment setup (company-wide unified settings)",
        docsUsageScenario4: "Easy configuration file creation for those who cannot write batch files",
        docsWarning: "Warning:",
        docsWarningDesc: "This setting modifies the registry and system settings. Before applying to production, please test in a test environment (1 PC). Especially when applying to multiple PCs, test on one PC first before deployment.",
        
        // Error
        settingsLoadError: "Failed to load settings data: {status}",
        
        // Language Switch
        language: "Language",
        languageJa: "日本語",
        languageEn: "English",
        headerOpenTable: "📊 Open table",
        headerOpenTableNewWindow: "🗔 New window",
        headerEditing: "Editing:",
        headerPrevPC: "Previous PC",
        headerNextPC: "Next PC",

        // Table Editor
        tableEditorPageTitle: "Table Editor | WIN BATCH GEN",
        tableEditorSubtitle: "Table Editor (Per-PC settings view)",
        tableEditorModalTitle: "📊 Table Editor (Per-PC settings)",
        tableEditorBackToMain: "Back to main",
        tableEditorBackToMainTitle: "Open main screen (same tab)",
        tableEditorHelpText: "Each column represents one PC. Edit settings and variables per row.",
        tableEditorAddPC: "Add PC",
        tableEditorClearTable: "Clear table",
        tableEditorSettingVariableHeader: "Setting / Variable",
        tableEditorPcNamePlaceholder: "PC name",
        tableEditorSelect: "Select",
        tableEditorSelected: "Selected",
        tableEditorSelectTitle: "Select this PC and apply to the main screen",
        tableEditorDeleteTitle: "Delete PC",
        tableEditorValuePlaceholder: "Value",
        tableEditorNoSettingsLoaded: "Settings are not loaded.",
        tableEditorNoPcs: "No PCs are registered. Add one using the \"Add PC\" button.",
        tableEditorClearConfirm: "Delete all table data?",

        // Generate batch files from table
        tableBatchSettingsNotLoaded: "Settings data is not loaded",
        tableBatchNoPcs: "No PCs are registered in the table. Add one using the \"Add PC\" button.",
        tableBatchConfirmMultiDownload: "Generate {count} batch files.\n\nEach file will be downloaded sequentially.\n\nContinue?",
        tableBatchDownloadComplete: "All file downloads are complete."
    }
};

// 現在の言語（デフォルトはブラウザの言語設定に基づく）
let currentLang: 'ja' | 'en' = 'ja';

// ストレージキー
const STORAGE_KEY_LANGUAGE = 'winbatchgen_language';

/**
 * ブラウザの言語設定を検出してデフォルト言語を決定
 * @returns 'ja' または 'en'
 */
function detectBrowserLanguage(): 'ja' | 'en' {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    // 日本語圏（ja, ja-JP等）の場合は日本語、それ以外は英語
    if (browserLang.startsWith('ja')) {
        return 'ja';
    }
    return 'en';
}

/**
 * 言語を初期化（ストレージから読み込むか、ブラウザ設定を検出）
 */
function initLanguage(): void {
    const stored = localStorage.getItem(STORAGE_KEY_LANGUAGE);
    if (stored && (stored === 'ja' || stored === 'en')) {
        currentLang = stored as 'ja' | 'en';
    } else {
        currentLang = detectBrowserLanguage();
        localStorage.setItem(STORAGE_KEY_LANGUAGE, currentLang);
    }
}

/**
 * 翻訳を取得
 * @param key - 翻訳キー
 * @param params - 置換パラメータ（オプション）
 * @param fallback - フォールバック値（キーが存在しない場合）
 * @returns 翻訳されたテキスト
 */
export function t(key: TranslationKey, params: Record<string, string | number> = {}, fallback: string | null = null): string {
    // 現在の言語の翻訳を取得
    let translation: string | undefined = translations[currentLang]?.[key];
    
    // 現在の言語に翻訳がない場合、英語の翻訳を試す
    if (!translation) {
        translation = translations['en']?.[key];
    }
    
    // 翻訳が見つからない場合
    if (!translation) {
        // フォールバックが指定されていればそれを使う
        if (fallback !== null) {
            translation = fallback;
        } else {
            // フォールバックもなければキーをそのまま返す
            translation = key;
        }
    }
    
    // パラメータ置換
    let result = translation;
    for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
    }
    
    return result;
}

// 設定データへの参照（app.jsから設定される）
let settingsData: SettingsData | null = null;

/**
 * 設定データを設定（app.jsから呼び出される）
 * @param settings - 設定データ
 */
export function setSettingsData(settings: SettingsData): void {
    settingsData = settings;
}

/**
 * タイトル（文字列 or 辞書）から現在の言語の値を取得
 */
function getTitleFromFallback(fallback: string | Record<string, string>): string {
    if (typeof fallback === 'string') {
        return fallback;
    }
    const lang = getCurrentLanguage();
    // 辞書の場合は現在の言語を優先、なければ ja、それもなければ最初の値
    return fallback[lang] || fallback['ja'] || Object.values(fallback)[0] || '';
}

/**
 * 任意のテキスト（文字列 or 辞書）から現在の言語の値を取得
 */
export function getLocalizedText(fallback: string | Record<string, string>): string {
    if (typeof fallback === 'string') {
        return fallback;
    }
    const lang = getCurrentLanguage();
    return fallback[lang] || fallback['ja'] || Object.values(fallback)[0] || '';
}

/**
 * 設定項目のタイトルを取得（翻訳対応）
 * @param id - 設定項目ID
 * @param fallback - フォールバック値（文字列 or 言語コードをキーにした辞書）
 * @returns 翻訳されたタイトル
 */
export function getSettingTitle(id: string, fallback: string | Record<string, string>): string {
    const lang = getCurrentLanguage();
    
    // 1. id ベースの i18n 辞書を優先（将来の DB / 外部リソース前提）
    const byId = settingTexts[lang]?.[id];
    if (byId && byId.title) {
        return byId.title;
    }

    // 2. 従来の settings.json 内 translations をフォールバックとして参照
    if (!settingsData) {
        return getTitleFromFallback(fallback);
    }
    
    // settings.jsonから該当する項目を検索（互換保持のため残す）
    for (const category of settingsData.categories || []) {
        for (const section of category.sections || []) {
            for (const item of section.items || []) {
                if (item.id === id) {
                    // translationsオブジェクトが存在する場合
                    if (item.translations && item.translations[lang]) {
                        return item.translations[lang].title || getTitleFromFallback(fallback);
                    }
                    // item.title が辞書形式の場合
                    if (typeof item.title === 'object') {
                        return getTitleFromFallback(item.title);
                    }
                    // 翻訳がない場合はフォールバック
                    return getTitleFromFallback(fallback);
                }
            }
        }
    }
    return getTitleFromFallback(fallback);
}

/**
 * 説明（文字列 or 辞書）から現在の言語の値を取得
 */
function getDescriptionFromFallback(fallback: string | Record<string, string>): string {
    if (typeof fallback === 'string') {
        return fallback;
    }
    const lang = getCurrentLanguage();
    // 辞書の場合は現在の言語を優先、なければ ja、それもなければ最初の値
    return fallback[lang] || fallback['ja'] || Object.values(fallback)[0] || '';
}

/**
 * 設定項目の説明を取得（翻訳対応）
 * @param id - 設定項目ID
 * @param fallback - フォールバック値（文字列 or 言語コードをキーにした辞書）
 * @returns 翻訳された説明
 */
export function getSettingDescription(id: string, fallback: string | Record<string, string>): string {
    const lang = getCurrentLanguage();
    
    // 1. id ベースの i18n 辞書
    const byId = settingTexts[lang]?.[id];
    if (byId && byId.description) {
        return byId.description;
    }
    
    // 2. 従来の settings.json
    if (!settingsData) return getDescriptionFromFallback(fallback);
    
    // settings.jsonから該当する項目を検索
    for (const category of settingsData.categories || []) {
        for (const section of category.sections || []) {
            for (const item of section.items || []) {
                if (item.id === id) {
                    // translationsオブジェクトが存在する場合
                    if (item.translations && item.translations[lang]) {
                        return item.translations[lang].description || getDescriptionFromFallback(fallback);
                    }
                    // item.description が辞書形式の場合
                    if (typeof item.description === 'object') {
                        return getDescriptionFromFallback(item.description);
                    }
                    // 翻訳がない場合はフォールバック
                    return getDescriptionFromFallback(fallback);
                }
            }
        }
    }
    return getDescriptionFromFallback(fallback);
}

/**
 * コード内の [textKey] プレースホルダーを翻訳で置換
 * @param code - 元のコード
 * @param item - 設定項目（translations にアクセスするため）
 * @returns 置換後のコード
 */
function replaceCodePlaceholders(code: string, item: SettingItem): string {
    const lang = getCurrentLanguage();
    
    // [textKey] パターンを検出して置換
    return code.replace(/\[([a-zA-Z0-9_]+)\]/g, (match, textKey) => {
        // translations[lang][textKey] を探す（型定義上は title/description/code... 以外もあり得るため安全に参照）
        const langMap = (item.translations?.[lang] as Record<string, unknown> | undefined) ?? undefined;
        const value = langMap ? langMap[textKey] : undefined;
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
        // 見つからない場合はプレースホルダーをそのまま返す（エラーを避けるため）
        return match;
    });
}

/**
 * 設定項目のコードを取得（翻訳対応）
 * @param id - 設定項目ID
 * @param fallback - フォールバック値
 * @returns 翻訳されたコード
 */
export function getSettingCode(id: string, fallback: string): string {
    const lang = getCurrentLanguage();
    
    // 1. id ベースの i18n 辞書
    const byId = settingTexts[lang]?.[id];
    if (byId && byId.code) {
        return byId.code;
    }

    // 2. 従来の settings.json
    if (!settingsData) {
        return fallback;
    }
    
    // settings.jsonから該当する項目を検索
    for (const category of settingsData.categories || []) {
        for (const section of category.sections || []) {
            for (const item of section.items || []) {
                if (item.id === id) {
                    let codeToReturn = fallback;
                    
                    // translationsオブジェクトが存在し、codeが含まれている場合
                    if (item.translations && item.translations[lang] && item.translations[lang].code) {
                        codeToReturn = item.translations[lang].code!;
                    }
                    
                    // コード内の [textKey] プレースホルダーを置換
                    return replaceCodePlaceholders(codeToReturn, item);
                }
            }
        }
    }
    return fallback;
}

/**
 * 設定項目の元に戻すコードを取得（翻訳対応）
 * @param id - 設定項目ID
 * @param fallback - フォールバック値
 * @returns 翻訳されたコード
 */
export function getSettingRevertCode(id: string, fallback: string): string {
    const lang = getCurrentLanguage();
    
    // 1. id ベースの i18n 辞書
    const byId = settingTexts[lang]?.[id];
    if (byId && byId.revertCode) {
        return byId.revertCode;
    }
    
    // 2. 従来の settings.json
    if (!settingsData) {
        return fallback;
    }
    
    // settings.jsonから該当する項目を検索
    for (const category of settingsData.categories || []) {
        for (const section of category.sections || []) {
            for (const item of section.items || []) {
                if (item.id === id) {
                    let codeToReturn = fallback;
                    
                    // translationsオブジェクトが存在し、revertCodeが含まれている場合
                    if (item.translations && item.translations[lang] && item.translations[lang].revertCode) {
                        codeToReturn = item.translations[lang].revertCode!;
                    }
                    
                    // コード内の [textKey] プレースホルダーを置換
                    return replaceCodePlaceholders(codeToReturn, item);
                }
            }
        }
    }
    return fallback;
}

/**
 * カテゴリのタイトルを取得（翻訳対応）
 * @param title - カテゴリタイトル（文字列 or 辞書）
 * @param fallback - フォールバック値（文字列 or 辞書）
 * @returns 翻訳されたタイトル
 */
export function getCategoryTitle(title: string | Record<string, string>, fallback: string | Record<string, string>): string {
    if (!settingsData) return getTitleFromFallback(fallback);
    
    const lang = getCurrentLanguage();
    
    // settings.jsonから該当するカテゴリを検索
    for (const category of settingsData.categories || []) {
        // title が文字列の場合：完全一致で検索
        // title が辞書の場合：category.title が辞書なら比較
        const matches = typeof title === 'string' 
            ? (typeof category.title === 'string' ? category.title === title : false)
            : (typeof category.title === 'object' && JSON.stringify(category.title) === JSON.stringify(title));
            
        if (matches) {
            // translationsオブジェクトが存在する場合
            if (category.translations && category.translations[lang]) {
                return category.translations[lang].title || getTitleFromFallback(fallback);
            }
            // category.title が辞書形式の場合
            if (typeof category.title === 'object') {
                return getTitleFromFallback(category.title);
            }
            // 翻訳がない場合はフォールバック
            return getTitleFromFallback(fallback);
        }
    }
    return getTitleFromFallback(fallback);
}

/**
 * セクションのタイトルを取得（翻訳対応）
 * @param title - セクションタイトル（文字列 or 辞書）
 * @param fallback - フォールバック値（文字列 or 辞書）
 * @returns 翻訳されたタイトル
 */
export function getSectionTitle(title: string | Record<string, string>, fallback: string | Record<string, string>): string {
    if (!settingsData) return getTitleFromFallback(fallback);
    
    const lang = getCurrentLanguage();
    
    // settings.jsonから該当するセクションを検索
    for (const category of settingsData.categories || []) {
        for (const section of category.sections || []) {
            // title が文字列の場合：完全一致で検索
            // title が辞書の場合：section.title が辞書なら比較
            const matches = typeof title === 'string'
                ? (typeof section.title === 'string' ? section.title === title : false)
                : (typeof section.title === 'object' && JSON.stringify(section.title) === JSON.stringify(title));
                
            if (matches) {
                // translationsオブジェクトが存在する場合
                if (section.translations && section.translations[lang]) {
                    return section.translations[lang].title || getTitleFromFallback(fallback);
                }
                // section.title が辞書形式の場合
                if (typeof section.title === 'object') {
                    return getTitleFromFallback(section.title);
                }
                // 翻訳がない場合はフォールバック
                return getTitleFromFallback(fallback);
            }
        }
    }
    return getTitleFromFallback(fallback);
}

/**
 * 現在の言語を取得
 * @returns 現在の言語コード ('ja' または 'en')
 */
export function getCurrentLanguage(): 'ja' | 'en' {
    return currentLang;
}

/**
 * 言語を切り替え
 * @param lang - 言語コード ('ja' または 'en')
 */
export function setLanguage(lang: string): void {
    if (lang !== 'ja' && lang !== 'en') {
        console.warn(`Invalid language: ${lang}. Using 'en' instead.`);
        lang = 'en';
    }
    currentLang = lang as 'ja' | 'en';
    localStorage.setItem(STORAGE_KEY_LANGUAGE, currentLang);
    
    // 言語変更イベントを発火
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { lang: currentLang } }));
}

/**
 * i18nを初期化
 */
export function initI18n(): void {
    initLanguage();
}

// 初期化
initI18n();



